<script setup lang="ts">
import type { TagsInputRootProps } from 'radix-vue'
import { ComboboxAnchor, ComboboxInput, ComboboxViewport } from 'radix-vue'
import { ref, computed } from 'vue'

import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList, Icon } from '@/components/ui'

import { TagsInput } from '.'

interface Props {
  modelValue: TagsInputRootProps['modelValue']
  availableTags?: string[]
  placeholder?: string
  emptyMessage?: string
  classNames?: string
}

const props = withDefaults(defineProps<Props>(), {
  availableTags: () => [],
  placeholder: 'Add tags...',
  emptyMessage: 'Tag already added.',
  classNames: '',
})

const emits = defineEmits<{
  (e: 'update:modelValue', val: TagsInputRootProps['modelValue']): void
}>()

const open = ref(false)
const searchTerm = ref('')

const filteredTags = computed(() => {
  const selected = props.modelValue || []
  const filtered = props.availableTags
    .filter(tag => !selected.includes(tag))
    .filter(tag => tag.toLowerCase().includes(searchTerm.value.toLowerCase()))

  const trimmedSearch = searchTerm.value.trim()
  if (
    trimmedSearch &&
    !filtered.some(tag => tag.toLowerCase() === trimmedSearch.toLowerCase()) &&
    !selected.includes(trimmedSearch)
  ) {
    return [trimmedSearch, ...filtered]
  }

  return filtered
})

function handleSelectTag(tag: string) {
  const current = props.modelValue || []
  if (!current.includes(tag)) {
    emits('update:modelValue', [...current, tag])
  }
  searchTerm.value = ''
}
</script>

<template>
  <TagsInput
    :model-value="modelValue"
    variant="blue"
    :class="classNames"
    @update:model-value="emits('update:modelValue', $event)"
  >
    <Command
      v-model:open="open"
      v-model:search-term="searchTerm"
      :default-open="false"
      class="relative block h-auto w-auto min-w-[240px] overflow-visible bg-transparent"
    >
      <ComboboxAnchor as-child>
        <ComboboxInput
          :placeholder="placeholder"
          class="min-h-6 w-full flex-1 bg-transparent px-1 text-sm focus:outline-none"
        />
      </ComboboxAnchor>

      <CommandList
        align="start"
        position="popper"
        dismissable
        class="bg-popover absolute top-0 z-20 mt-1.5 w-max min-w-[240px] overflow-hidden rounded-md border shadow-[0px_10px_38px_-10px_rgba(22,_23,_24,_0.35),_0px_10px_20px_-15px_rgba(22,_23,_24,_0.2)]"
      >
        <ComboboxViewport class="relative max-h-[300px] min-h-8">
          <CommandEmpty class="py-2 text-center text-xs font-medium">
            {{ emptyMessage }}
          </CommandEmpty>

          <CommandGroup v-if="filteredTags.length > 0">
            <CommandItem
              v-for="(tag, i) in filteredTags"
              :key="tag"
              :value="tag"
              class="whitespace-nowrap"
              @select="handleSelectTag(tag)"
            >
              <template v-if="!i && !availableTags.includes(tag)">
                <Icon name="PlusIcon" class="mr-2 size-4" />
                Create “{{ tag }}”
              </template>
              <template v-else>
                {{ tag }}
              </template>
            </CommandItem>
          </CommandGroup>
        </ComboboxViewport>
      </CommandList>
    </Command>
  </TagsInput>
</template>
